import React, { createContext, useContext, useMemo } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import { AppFunction, functionRoleMap, UserRole } from '../auth/roles'

type AccessLevelContextType = {
  userRoles: UserRole[]
  isAuthorized: (action: AppFunction) => boolean
}

const AccessLevelContext = createContext<AccessLevelContextType | undefined>(
  undefined
)

export function AccessLevelProvider({
  children
}: {
  children: React.ReactNode
}) {
  const { authState } = useOktaAuth()

  const userRoles: UserRole[] = useMemo(() => {
    const claims = authState?.accessToken?.claims
    const roles = claims?.groups || []

    return Array.isArray(roles)
      ? (roles.filter((role): role is UserRole =>
          Object.values(UserRole).includes(role as UserRole)
        ) as UserRole[])
      : []
  }, [authState])
  const isAuthorized = (action: AppFunction): boolean =>
    functionRoleMap[action].some((role) => userRoles.includes(role))

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = useMemo(() => ({ userRoles, isAuthorized }), [userRoles])

  return (
    <AccessLevelContext.Provider value={value}>
      {children}
    </AccessLevelContext.Provider>
  )
}

export const useAccessLevel = (): AccessLevelContextType => {
  const context = useContext(AccessLevelContext)
  if (!context) {
    throw new Error('useAccessLevel must be used within an AccessLevelProvider')
  }
  return context
}
